/**
 * Geometry Service
 * 
 * Contains geometric calculations and utilities used throughout the application.
 */

/**
 * Calculate the distance between two points (chord length)
 * 
 * @param {number} x1 - X coordinate of first point
 * @param {number} y1 - Y coordinate of first point
 * @param {number} x2 - X coordinate of second point
 * @param {number} y2 - Y coordinate of second point
 * @returns {number} Distance between the two points
 */
export const calculateDistance = (x1, y1, x2, y2) => {
    const dx = x2 - x1;
    const dy = y2 - y1;
    return Math.sqrt(dx * dx + dy * dy);
};

/**
 * Calculate the midpoint between two points
 * 
 * @param {number} x1 - X coordinate of first point
 * @param {number} y1 - Y coordinate of first point
 * @param {number} x2 - X coordinate of second point
 * @param {number} y2 - Y coordinate of second point
 * @returns {Object} Midpoint coordinates {x, y}
 */
export const calculateMidpoint = (x1, y1, x2, y2) => {
    return {
        x: (x1 + x2) / 2,
        y: (y1 + y2) / 2
    };
};

/**
 * Calculate perpendicular unit vector to a line between two points
 * 
 * @param {number} x1 - X coordinate of first point
 * @param {number} y1 - Y coordinate of first point
 * @param {number} x2 - X coordinate of second point
 * @param {number} y2 - Y coordinate of second point
 * @returns {Object} Perpendicular unit vector {x, y}
 */
export const calculatePerpendicularVector = (x1, y1, x2, y2) => {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const length = Math.sqrt(dx * dx + dy * dy);
    
    if (length === 0) {
        return { x: 0, y: 0 };
    }
    
    // Perpendicular vector (rotated 90° clockwise)
    return {
        x: dy / length,
        y: -dx / length
    };
};

/**
 * Calculate circle radius from chord length and sagitta
 * 
 * @param {number} chordLength - Length of the chord
 * @param {number} sagitta - Sagitta (height of arc)
 * @returns {number} Circle radius
 */
export const calculateCircleRadius = (chordLength, sagitta) => {
    if (sagitta <= 0) return 0;
    return (chordLength * chordLength) / (8 * sagitta) + sagitta / 2;
};

/**
 * Calculate cutoff points and dimensions from cutoff properties
 * 
 * @param {Object} cutoffProperties - Cutoff configuration {pos_tip_left, pos_right_bottom, pos_left_bottom, curve_right, curve_left}
 * @param {number} plateX - Plate X position in canvas pixels
 * @param {number} plateY - Plate Y position in canvas pixels
 * @param {number} plateWidth - Plate width in canvas pixels
 * @param {number} plateHeight - Plate height in canvas pixels
 * @param {number} scale - Scale factor from mm to pixels
 * @returns {Object} Calculated cutoff points and pixel dimensions
 */
export const calculateCutoffPoints = (cutoffProperties, plateX, plateY, plateWidth, plateHeight, scale) => {
    // Convert mm values to pixels
    const cutoffTopPx = cutoffProperties.pos_tip_left * scale;
    const cutoffRightPx = cutoffProperties.pos_right_bottom * scale;
    const cutoffLeftBottomPx = (cutoffProperties.pos_left_bottom || 0) * scale;
    const curveRadiusPx = (cutoffProperties.curve_right || 0) * scale;
    const curveRadiusBottomPx = (cutoffProperties.curve_left || 0) * scale;
    
    // Calculate cutoff points in canvas coordinates
    // Point 1: x distance from left edge of top (tip)
    const point1X = plateX + cutoffTopPx;
    const point1Y = plateY;
    
    // Point 2: y distance from bottom of right edge
    const point2X = plateX + plateWidth;
    const point2Y = plateY + plateHeight - cutoffRightPx;
    
    // Point 3: y distance from bottom of left edge (only when pos_left_bottom > 0)
    const point3X = plateX;
    const point3Y = plateY + plateHeight - cutoffLeftBottomPx;
    
    // Determine which lines to draw
    const hasLeftLine = cutoffProperties.pos_left_bottom > 0;
    const hasRightLine = cutoffProperties.pos_right_bottom > 0;
    
    return {
        // Pixel dimensions
        cutoffTopPx,
        cutoffRightPx,
        cutoffLeftBottomPx,
        curveRadiusPx,
        curveRadiusBottomPx,
        
        // Canvas coordinate points
        point1: { x: point1X, y: point1Y }, // tip point
        point2: { x: point2X, y: point2Y }, // right bottom point
        point3: { x: point3X, y: point3Y }, // left bottom point
        
        // Line existence flags
        hasLeftLine,
        hasRightLine
    };
}; 


/**
 * Calculate the diagonal line length of a square
 * 
 * @param {number} width - square width
 * @param {number} height - square height
 * @returns {number} Diagonal length of the square
 */
export const calculateSquareDiagonal = (width, height) => {
    return Math.sqrt(width * width + height * height);
};

/**
 * Calculate sagitta (perpendicular distance from chord to arc) 
 * 
 * @param {number} radius - Radius of the circle
 * @param {number} chordLength - Length of the chord
 * @returns {number} Sagitta distance, or 0 if invalid input
 */
export const calculateSagitta = (radius, chordLength) => {
    if (radius <= 0 || chordLength <= 0 || chordLength > 2 * radius) {
        return 0;
    }
    const halfChord = chordLength / 2;
    return radius - Math.sqrt(radius * radius - halfChord * halfChord);
};